import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { EuiButton, EuiButtonEmpty, EuiConfirmModal, EuiFlexGroup, EuiFlexItem, EuiHideFor, EuiInMemoryTable, EuiLoadingSpinner, EuiOverlayMask, EuiSpacer, EuiSwitch, EuiText } from '@elastic/eui'
import { EuiBasicTableColumn } from '@elastic/eui/src/components/basic_table/basic_table'

import { AcImage, CampaignActiveStatus, CampaignStatus, Creative, CreativeStatus, WhiteLabel } from 'api/interfaces'
import { useDeleteCreativeMutation, useGetCampaignCreativesQuery } from 'api/rtkQueryApi/platform/creativesApi'
import { RootState } from 'app/rootReducer'
import { useEnterpriseSupport } from 'hooks/useEnterpriseSupport'
import LastCreativePausedErrorModal from 'features/campaigns/campaign/LastCreativePausedErrorModal'
import AgencyV2Modal from 'features/planner/components/AgencyV2Modal'
import history from 'services/HistoryService'

import { DEFAULT_INITIAL_PAGE_SIZE, DEFAULT_PAGE_SIZE_OPTIONS } from '../../../../utils/DefaultPageSize'
import { useWhiteLabel } from '../../../../whiteLabel/WhiteLabelContext'
import { removeCampaign } from '../../campaigns/campaignsSlice'
import { CreateAdStep, CreateAdStyle, resetCreateAd, setAd, setCreateAdStep, setCreateAdStyle, setDisplayAd, setImage, setIsCopy } from '../../createAd/internet/retail/createAdInternetRetailSlice'
import { AdState, fetchAds, pauseAd, removeAd, startAd } from '../campaignSlice'
import { EditDisplayAdModal } from './EditDisplayAdModal'
import { buildAdColumns, toAdState } from './adsGridColumns'

interface AdsDrawerProps {
  CreateAdButtonText?: string
  OnCreateAdClick?: () => void
}

const AdsDrawer = ({ CreateAdButtonText = 'Create Ad', OnCreateAdClick }: AdsDrawerProps) => {
  const dispatch = useDispatch()
  const campaign = useSelector((state: RootState) => state.campaign)
  const { currentAccount } = useSelector((state: RootState) => state.app)
  const [allowAdCreation, setAllowAdCreation] = useState(true)
  const [isRemoveModalVisible, setIsRemoveModalVisible] = useState(false)
  const [adIdToRemove, setAdIdToRemove] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(0)
  const [isLastCreativePausedErrorVisible, setIsLastCreativePausedErrorVisible] = useState(false)
  const getCampaignsCreativesQuery = useGetCampaignCreativesQuery({
    accountId: currentAccount!.id,
    campaignId: campaign.campaign!.id
  })
  const [campaignCreatives, setCampaignCreatives] = useState<Creative[]>([])
  const [adStates, setAdStates] = useState<AdState[]>([])
  const whiteLabel = useWhiteLabel()
  const [creativeToDelete, setCreativeToDelete] = useState<string>()
  const [deleteCreative, deleteCreativeResult] = useDeleteCreativeMutation()
  const isEnterpriseOrganization = useEnterpriseSupport()
  const [creativeToEdit, setCreativeToEdit] = useState<Creative | null>(null)
  const isInternetCampaign = campaign.campaign?.type === 'Internet'
  const isEditableDraft = campaign.campaign?.status === CampaignStatus.SavedForLater || campaign.campaign?.status === CampaignStatus.Disapproved
  const canEditDisplayAds = isEnterpriseOrganization && isInternetCampaign && campaign.campaign?.activeStatus === CampaignActiveStatus.Inactive
  const canDeleteAds = isEditableDraft && (whiteLabel?.getWhiteLabel() === WhiteLabel.AgencyV2 || isEnterpriseOrganization)

  useEffect(() => {
    if (campaign.campaign) {
      setAllowAdCreation(campaign.campaign.targetingTemplate !== 'Recruitment' && campaign.campaign.targetingTemplate !== 'Consumer' && campaign.campaign.status !== CampaignStatus.WaitingForCustomAds)
    }
  }, [campaign.campaign])

  useEffect(() => {
    if (getCampaignsCreativesQuery.data) {
      setCampaignCreatives(getCampaignsCreativesQuery.data)
    }

    if (creativeToDelete === 'deleted') {
      setCreativeToDelete(undefined)
    }
  }, [getCampaignsCreativesQuery.data])

  useEffect(() => {
    let aStates = campaignCreatives.map(toAdState)
    aStates = aStates.filter(a => !(a.type === 'Internet' && (a.status === CreativeStatus.Inactive || a.status === CreativeStatus.Deleted) && (a.headline || a.body)))
    setAdStates(aStates)
  }, [campaignCreatives])

  const onConfirmDeleteClick = async () => {
    if (!creativeToDelete) {
      return
    }

    const result = await deleteCreative({ accountId: currentAccount!.id, campaignId: campaign.campaign!.id, creativeId: creativeToDelete })

    if (result) {
      setCreativeToDelete('deleted')
    }
  }

  const cancelRemoveModal = () => setIsRemoveModalVisible(false)

  const confirmRemoveModal = () => {
    if (adIdToRemove && currentAccount && campaign && campaign.campaign) {
      if (campaignCreatives.length === 1) {
        dispatch(removeCampaign(currentAccount.id, campaign.campaign.id))
        setIsRemoveModalVisible(false)
        setAdIdToRemove(null)
        history.push('/campaigns')
      } else {
        dispatch(removeAd(currentAccount.id, campaign.campaign.id, adIdToRemove))
        setIsRemoveModalVisible(false)
        setAdIdToRemove(null)
      }
    }
  }

  const showRemoveModal = (adId: string) => {
    setIsRemoveModalVisible(true)
    setAdIdToRemove(adId)
  }

  let removeModal

  if (isRemoveModalVisible) {
    removeModal = (
      <EuiOverlayMask>
        <EuiConfirmModal title='Are you sure you want to remove this ad?' onCancel={cancelRemoveModal} onConfirm={confirmRemoveModal} cancelButtonText="No, don't do it" confirmButtonText='Yes, do it' defaultFocusedButton='confirm' buttonColor='danger'>
          {campaignCreatives.length === 1 ? <p>Once you do this, this campaign will be removed and you will need to recreate it if you made a mistake.</p> : <p>Once you do this, this ad will be removed and you will need to recreate it if you made a mistake.</p>}
        </EuiConfirmModal>
      </EuiOverlayMask>
    )
  }

  const createAd = () => {
    dispatch(resetCreateAd())
    OnCreateAdClick?.()
  }

  const cloneAd = (adId: string) => {
    const selectedAd = campaignCreatives.find(a => a.id === adId)

    if (!!selectedAd) {
      dispatch(
        setImage({
          id: '',
          highQualityUrl: selectedAd.highQualityUrl,
          squareUrl: selectedAd.squareUrl,
          thumbnailUrl: selectedAd.thumbnailUrl,
          isPurchased: false,
          isUploaded: false
        } as AcImage)
      )
      dispatch(
        setAd({
          id: '',
          headline: selectedAd.nativeDetails.headline,
          body: selectedAd.nativeDetails.body,
          companyName: selectedAd.nativeDetails.companyName,
          callToAction: selectedAd.nativeDetails.callToAction,
          destinationUrl: selectedAd.destinationUrl
        })
      )
      dispatch(setDisplayAd(null))
      dispatch(setIsCopy())
      dispatch(setCreateAdStyle(CreateAdStyle.Build))
      dispatch(setCreateAdStep(CreateAdStep.Create))
      history.push(`/campaigns/internet/public/create`)
    }
  }

  const columns: Array<EuiBasicTableColumn<AdState>> = [
    {
      width: '90px',
      name: 'ON/OFF',
      render: (ad: AdState) => (
        <React.Fragment>
          <EuiSwitch
            showLabel={false}
            label={''}
            checked={ad.checked}
            disabled={ad.disabled}
            onChange={e => {
              if (e.target.checked) {
                dispatch(startAd(currentAccount!.id, campaign!.campaign!.id, ad.id))
                setAdStates(adStates.map(a => (a.id === ad.id ? { ...a, checked: true } : a)))
              } else {
                if (adStates.filter(a => a.checked).length <= 1) {
                  setIsLastCreativePausedErrorVisible(true)
                  return
                }

                dispatch(pauseAd(currentAccount!.id, campaign!.campaign!.id, ad.id))
                setAdStates(adStates.map(a => (a.id === ad.id ? { ...a, checked: false } : a)))
              }
            }}
          />
          {ad.loading && <EuiLoadingSpinner style={{ marginLeft: 5 }} />}
        </React.Fragment>
      )
    },
    ...buildAdColumns(adStates, isInternetCampaign, isEnterpriseOrganization),
    {
      width: '215px',
      name: '',
      render: (ad: AdState) => (
        <React.Fragment>
          <EuiButtonEmpty
            id='view'
            iconType='inspect'
            onClick={() => {
              history.push(`/campaigns/ads/${campaign.campaign!.id}/edit/${ad.id}`)
            }}
            size='s'>
            View
          </EuiButtonEmpty>
          {ad.mediaType === 'Display' && canEditDisplayAds && (
            <EuiButtonEmpty id='edit' iconType='pencil' onClick={() => setCreativeToEdit(campaignCreatives.find(creative => creative.id === ad.id) ?? null)} size='s'>
              Edit
            </EuiButtonEmpty>
          )}
          {ad.mediaType === 'Display' ? (
            <></>
          ) : (
            <EuiButtonEmpty id='copy' iconType='copy' onClick={() => cloneAd(ad.id)} size='s'>
              Copy
            </EuiButtonEmpty>
          )}
          {ad.canRemove && (
            <EuiButtonEmpty id='remove' iconType='cross' onClick={() => showRemoveModal(ad.id)} size='s'>
              Remove
            </EuiButtonEmpty>
          )}
        </React.Fragment>
      )
    },
    {
      name: 'Actions',
      hidden: !canDeleteAds,
      actions: [
        {
          name: 'Delete',
          description: 'Delete',
          icon: 'trash',
          type: 'icon',
          color: 'danger',
          onClick: (item: AdState) => {
            setCreativeToDelete(item.id)
          },
          enabled: () => canDeleteAds,
          available: () => !deleteCreativeResult.isLoading || !campaign.isLoadingAds
        }
      ]
    }
  ]
  const pagination = {
    initialPageSize: DEFAULT_INITIAL_PAGE_SIZE,
    pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
    pageIndex: currentPage
  }

  return (
    <React.Fragment>
      <EuiSpacer />
      <EuiFlexGroup id='adsDrawerMediaQuery'>
        <EuiFlexItem>
          {allowAdCreation && (
            <EuiFlexGroup alignItems='center'>
              <EuiFlexItem />
              <EuiFlexItem grow={false}>
                <EuiButton id='create' size='s' key='createAd' onClick={createAd} fill>
                  {CreateAdButtonText}
                </EuiButton>
              </EuiFlexItem>
            </EuiFlexGroup>
          )}
          <EuiInMemoryTable
            loading={getCampaignsCreativesQuery.isLoading}
            items={adStates}
            columns={columns}
            pagination={pagination}
            responsive={false}
            onChange={(criteria: any) => {
              setCurrentPage(criteria.page.index)
            }}
          />
        </EuiFlexItem>
        {whiteLabel?.getWhiteLabel() !== WhiteLabel.AgencyV2 && !isEnterpriseOrganization && (
          <EuiHideFor sizes={['m', 'l']}>
            <EuiFlexItem grow={false} style={{ width: 270 }}>
              <EuiText size='xs'>
                <h3>Best Practices</h3>
                <h5>Keeping Your Ads Fresh</h5>
                <p>It is important to keep an eye on your ads and make sure they are not getting stale and/or overexposed. If you have had an ad running for a while, consider creating another ad as part of your campaign.</p>
              </EuiText>
            </EuiFlexItem>
          </EuiHideFor>
        )}
      </EuiFlexGroup>
      {removeModal}
      <LastCreativePausedErrorModal isModalVisible={isLastCreativePausedErrorVisible} closeModal={() => setIsLastCreativePausedErrorVisible(false)} />
      {creativeToEdit && campaign.campaign && <EditDisplayAdModal campaignId={campaign.campaign.id} creative={creativeToEdit} onClose={() => setCreativeToEdit(null)} />}
      {creativeToDelete && (
        <AgencyV2Modal isLoading={deleteCreativeResult.isLoading || getCampaignsCreativesQuery.isFetching} title='Remove Ad' onClose={() => setCreativeToDelete(undefined)} confirmButtonText='Remove' onConfirmButtonClick={onConfirmDeleteClick} showCancelButton>
          <EuiText>This ad will be removed from the campaign.</EuiText>
        </AgencyV2Modal>
      )}
    </React.Fragment>
  )
}

export default AdsDrawer
